/* global globalThis */
import {HubConnectionBuilder, LogLevel} from '@microsoft/signalr';

import config from '../../configuration';
import authentication from '../authentication';
import trace from '../trace';
import handler from './handler';

// Reconnect backoff. The default withAutomaticReconnect() gives up after four
// attempts (~42s); returning a delay for every attempt keeps retrying for as
// long as the app is open.
const RETRY_DELAYS_MS = [0, 2000, 5000, 10000, 30000];

const nextRetryDelay = ({previousRetryCount = 0} = {}) =>
  RETRY_DELAYS_MS[Math.min(previousRetryCount, RETRY_DELAYS_MS.length - 1)];

// Delay before rebuilding a connection that closed despite the retry policy.
const RESTART_DELAY_MS = 30 * 1000;

let connection = null;
let restartTimer = null;
let stopped = false;

const clearRestartTimer = () => {
  if (restartTimer) {
    globalThis.clearTimeout?.(restartTimer);
    restartTimer = null;
  }
};

const scheduleRestart = (delay) => {
  if (stopped || restartTimer) {
    return;
  }

  restartTimer = globalThis.setTimeout?.(() => {
    restartTimer = null;
    initialize().catch(trace);
  }, delay);
};

const initialize = async () => {
  trace('Initializing notifications');
  stopped = false;

  if (connection) {
    // Already connected (or connecting); never build a second hub.
    return;
  }

  const user_id = await authentication.getUserID();

  if (!user_id) {
    scheduleRestart(60 * 1000); // User id not found, retry after 1 minute.
    return;
  }

  const built = new HubConnectionBuilder()
    .withUrl(config.signalRHubUrl + '/service_request/notification', {
      headers: {
        'x-ms-client-principal-id': user_id,
      },
      // Resolved on every (re)connect: a token captured once goes stale and
      // makes each reconnect fail authentication.
      accessTokenFactory: () => authentication.getAutoRefreshAccessToken(),
    })
    .withAutomaticReconnect({nextRetryDelayInMilliseconds: nextRetryDelay})
    .configureLogging(LogLevel.Information)
    .build();

  connection = built;

  built.on('notification', (notification) => {
    try {
      trace('Notification received', notification);

      handler(notification);
    } catch (e) {
      console.log(e);
    }
  });

  built.onclose(() => {
    // Do NOT reload the page here: that discards whatever the user was editing.
    // Drop the dead hub and rebuild it in the background instead.
    trace('Notifications disconnected, will reconnect');
    connection = null;
    scheduleRestart(RESTART_DELAY_MS);
  });

  try {
    await built.start();
    trace('Notifications connected');
  } catch (e) {
    trace('Notifications failed to connect', e);
    connection = null;
    scheduleRestart(RESTART_DELAY_MS);
  }
};

const notificationsApi = {
  initialize,
  kill: () => {
    stopped = true;
    clearRestartTimer();

    const active = connection;
    connection = null;

    return active?.stop?.();
  },
  register: async () => {
    await initialize();
  },
  requestPermissions: async () => {
    try {
      const result = await Notification.requestPermission();
      trace('Permission result', result);
    } catch {}
  },
};

export default notificationsApi;
